/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2025 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import XngZone from "@eisinc/App/Components/Common/XngZone";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
    // XNG-Changes
    breadcrumbsContainer?: InjectableCss;
    breadcrumbsAndLinksContainer?: InjectableCss;
    imageSection?: InjectableCss;
    rightBottomColumnGridItem?: GridItemProps;
    actions?: InjectableCss;
    shareLinksSection?: InjectableCss;
    documentsSection?: InjectableCss;
    specificationsSection?: InjectableCss;
    carouselSection?: InjectableCss;
    bannerSection?: InjectableCss;
    productOptions?: InjectableCss;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            overflow: hidden;
            direction: ltr;
        `,
    },
    topGridItem: { width: 12 },
    middleGridItem: { width: [12, 12, 0, 0, 0] },
    middleGridItemHidden: {
        above: "sm",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    bottomGridItem: { width: 12 },
    rightColumnGridItem: { width: [0, 0, 4, 4, 4] },
    rightColumnGridItemHidden: {
        below: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    container: {
        css: css`
            direction: rtl;
        `,
    },
};

const styles = viewStyles;

const ProductDetailsView: React.FC<WidgetProps> = ({ id }) => {
    const isShellEditingMode = useShellContext()?.isEditing;

    // XNG-Change: Restructure UI
    return (
        <>
            <StyledWrapper {...styles.breadcrumbsAndLinksContainer}>
                <StyledWrapper {...styles.breadcrumbsContainer}>
                    <XngZone contentId={id} zoneName="Content9" hint="Breadcrumb Zone" />
                </StyledWrapper>
                <StyledWrapper {...styles.shareLinksSection}>
                    <XngZone contentId={id} zoneName="Content1" hint="Share Links Zone" />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.bannerSection} {...{ isShellEditingMode }}>
                <XngZone contentId={id} zoneName="Banner Zone" hint="Banner Zone" />
            </StyledWrapper>
            <GridContainer {...styles.container}>
                <GridItem {...styles.leftColumnGridItem} data-test-selector="productDetails_leftColumn">
                    <StyledWrapper {...styles.imageSection}>
                        <XngZone contentId={id} zoneName="Content0" hint="Zone For Image" />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.rightColumnGridItem}>
                    <XngZone contentId={id} zoneName="Content4" hint="Zone For Info" />
                </GridItem>
                <GridItem {...styles.rightBottomColumnGridItem}>
                    <StyledWrapper {...styles.actions}>
                        <XngZone contentId={id} zoneName="Content3" hint="Zone For Actions" />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.bottomGridItem}>
                    <StyledWrapper {...styles.carouselSection} {...{ isShellEditingMode }}>
                        <XngZone contentId={id} zoneName="Content5" hint="RelatedProducts Carousel" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.productOptions} {...{ isShellEditingMode }}>
                        <XngZone contentId={id} zoneName="Content10" hint="productOptions" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.specificationsSection} {...{ isShellEditingMode }}>
                        <XngZone contentId={id} zoneName="Content6" hint="Description & Attributes" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.documentsSection} {...{ isShellEditingMode }}>
                        <XngZone contentId={id} zoneName="Content7" hint="Documents and authored content" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.carouselSection} {...{ isShellEditingMode }}>
                        <XngZone contentId={id} zoneName="Content8" hint="recentlyViewed carousel" />
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect()(ProductDetailsView),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
